/*  MUI  */
import {
    Form,
    FormFieldGroup,
    FormModal,
    FormModalBody,
    FormModalCancelButton,
    FormModalFooter,
    FormModalSubmitButton
} from '@auviknetworks/iceberg-ui';
import { type FieldSchemaJson as workatoSchema } from 'application/Shared/Components/Form/Field.type';
import { FieldFactory } from 'application/Shared/Components/Form/FieldFactory';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

const fieldFormDefinition: workatoSchema[] = [
    {
        control: {
            type: 'checkbox'
        },
        label: 'Optional',
        name: 'optional',
        optional: true,
        type: 'boolean'
    },
    {
        control: {
            type: 'text'
        },
        label: 'Name',
        name: 'name',
        optional: false,
        type: 'string'
    },
    {
        control: {
            type: 'text'
        },
        label: 'Label',
        name: 'label',
        optional: true,
        type: 'string'
    },
    {
        control: {
            options: {
                type: 'static',
                values: [
                    { label: 'Text', value: 'text' },
                    { label: 'Text-area', value: 'text-area' },
                    { label: 'Password', value: 'password' },
                    { label: 'Integer', value: 'integer' },
                    { label: 'Number', value: 'number' },
                    { label: 'Url', value: 'url' },
                    { label: 'Select', value: 'select' },
                    { label: 'Field group', value: 'field_group' },
                    { label: 'Key value', value: 'key_value' },
                    { label: 'Checkbox', value: 'checkbox' },
                    { label: 'Date', value: 'date' },
                    { label: 'Date time', value: 'date_time' },
                    { label: 'Phone', value: 'phone' },
                    { label: 'Email', value: 'email' },
                    { label: 'Subdomain', value: 'subdomain' },
                    { label: 'Key value', value: 'Key value' }
                ]
            },
            type: 'select'
        },
        label: 'Control',
        name: 'control.type',
        optional: true,
        type: 'string'
    },
    {
        control: {
            render: 'control.type == "select"',
            type: 'checkbox'
        },
        label: 'Allow Multiple Select',
        name: 'control.allowMultipleSelect',
        optional: true,
        type: 'boolean'
    },
    {
        control: {
            options: {
                type: 'static',
                values: [
                    { label: 'Static', value: 'static' },
                    { label: 'API', value: 'api' }
                ]
            },
            // We render only if control type is select
            render: 'control.type == "select"',
            type: 'select'
        },
        label: 'Select Options Type',
        name: 'control.options.type',
        optional: false,
        type: 'array'
    },
    {
        control: {
            // We render only if control type is select
            render: 'has(control.options) && control.options.type == "static"',
            type: 'key_value'
        },
        label: 'Select Options',
        name: 'control.options.values',
        optional: false,
        type: 'array'
    },
    {
        control: {
            options: {
                type: 'static',
                values: [
                    { label: 'Workato', value: 'workato' }
                    // TODO: Add ASM support
                    // { label: 'ASM', value: 'asm' }
                ]
            },
            // We render only if control type is select
            render: 'control.type == "select" && control.options.type == "api"',
            type: 'select'
        },
        label: 'API Service Type',
        name: 'control.options.operation.serviceType',
        optional: false,
        type: 'array'
    },
    {
        control: {
            render: 'has(control.options) && control.options.type == "api"',
            type: 'field_group'
        },
        label: 'API Details (operation)',
        name: 'control.options.operation',
        optional: false,
        properties: [
            {
                control: {
                    placeholder: 'getorgunits or getusers',
                    // We render only if control type is select
                    render: 'has(control.options) && control.options.operation.serviceType == "workato"',
                    type: 'text'
                },
                label: 'Recipe Action',
                name: 'action',
                optional: false,
                type: 'string'
            },
            {
                control: {
                    helperText:
                        // eslint-disable-next-line max-len
                        'The key from the response that will be used for the value of the selected option. This will be the value submitted with the API request.',
                    render: 'has(control.options) && control.options.operation.serviceType == "workato"',
                    type: 'text'
                },
                label: 'Value Accessor',
                name: 'valueAccessor',
                optional: false,
                type: 'string'
            },
            {
                control: {
                    helperText:
                        'The key from the response that will be used for the label.',
                    render: 'has(control.options) && control.options.operation.serviceType == "workato"',
                    type: 'text'
                },
                label: 'Label',
                name: 'label',
                optional: false,
                type: 'string'
            },
            {
                control: {
                    helperText:
                        // eslint-disable-next-line max-len
                        'If search is enabled, an API request with a search parameter will be made to fetch additional options. If the recipe returns a paginated response, you will likely want to enable this and set up the recipe to support search.',
                    render: 'has(control.options) && control.options.operation.serviceType == "workato"',
                    type: 'checkbox'
                },
                label: 'Enable Search via API?',
                name: 'search.enabled',
                optional: true,
                type: 'boolean'
            },
            {
                control: {
                    helperText:
                        'The parameter name that will be sent in the API request for searching.',
                    render: 'has(control.options) && control.options.operation.serviceType == "workato" && control.options.operation.search.enabled',
                    type: 'text'
                },
                label: 'Search Field Name',
                name: 'search.searchField',
                optional: false,
                type: 'string'
            }
        ],
        type: 'string'
    },
    {
        control: {
            options: {
                type: 'static',
                values: [
                    { label: 'String', value: 'string' },
                    { label: 'Integer', value: 'integer' },
                    { label: 'Number', value: 'number' },
                    { label: 'Date time', value: 'date_time' },
                    { label: 'Date', value: 'date' },
                    { label: 'Timestamp', value: 'timestamp' },
                    { label: 'Boolean', value: 'boolean' },
                    { label: 'Object', value: 'object' },
                    { label: 'Array', value: 'array' }
                ]
            },
            type: 'select'
        },
        label: 'Data type',
        name: 'type',
        optional: true,
        type: 'string'
    },
    {
        control: {
            type: 'text'
        },
        label: 'Hint',
        name: 'control.helperText',
        optional: true,
        type: 'string'
    }
];

export const FieldForm = ({
    defaultValues,
    handleClose,
    handleSubmit,
    isOpen
}: {
    defaultValues?: workatoSchema;
    handleClose: () => void;
    handleSubmit: (values: workatoSchema) => void;
    isOpen: boolean;
}) => {
    const { t } = useTranslation();

    const title = t(
        defaultValues
            ? 'Admin.FormBuilder.field.edit'
            : 'Admin.FormBuilder.field.add'
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- generic payload
    const validate = (values: any) => {
        const results: {
            errors: {
                [key: string]: {
                    message: string;
                    type: string;
                };
            };
            values: unknown;
        } = { errors: {}, values };

        // Validate required fields
        if (!values.name) {
            results.errors.name = {
                message: t('Form.fieldRequired'),
                type: 'required'
            };
        }

        return results;
    };

    return (
        <FormModal title={title} closeModal={handleClose} isOpen={isOpen}>
            <Form
                defaultValues={defaultValues}
                onSubmit={async (values) => {
                    await handleSubmit(values);
                    handleClose();
                }}
                // validate={validate}
            >
                <FormModalBody>
                    <FormFieldGroup>
                        {fieldFormDefinition.map((field: workatoSchema) => (
                            <FieldFactory key={field.name} schema={field} />
                        ))}
                    </FormFieldGroup>
                </FormModalBody>
                <FormModalFooter>
                    <FormModalCancelButton>
                        {t('Common.cancel')}
                    </FormModalCancelButton>
                    <FormModalSubmitButton>
                        {t('Common.save')}
                    </FormModalSubmitButton>
                </FormModalFooter>
            </Form>
        </FormModal>
    );
};
